export function isNeutralBoldWrapperFontWeight(fontWeight: string): boolean {
  const normalizedFontWeight = fontWeight.trim().toLowerCase()
  if (normalizedFontWeight === 'normal') return true

  const numericFontWeight = Number(normalizedFontWeight)
  return normalizedFontWeight.length > 0 && Number.isFinite(numericFontWeight) && numericFontWeight < 600
}

function isBoldFontWeight(fontWeight: string): boolean {
  const normalizedFontWeight = fontWeight.trim().toLowerCase()
  if (normalizedFontWeight === 'bold' || normalizedFontWeight === 'bolder') return true

  const numericFontWeight = Number(normalizedFontWeight)
  return normalizedFontWeight.length > 0 && Number.isFinite(numericFontWeight) && numericFontWeight >= 600
}

// Strips font/style information from pasted HTML, keeping only semantic formatting
// (bold, italic, underline, lists, headings, links). Prevents pasted content from
// Word, Google Docs, etc. from importing mismatched fonts and colors.
export function stripPastedFontStyles(html: string): string {
  if (typeof document === 'undefined') return html
  const template = document.createElement('template')
  template.innerHTML = html

  // Step 1: Unwrap Google Docs neutralizer wrappers, e.g. <b style="font-weight:normal">
  // or <b style="font-weight:400"> and <i style="font-style:normal"> are used as
  // layout containers, not semantic marks. Must run before stripping styles.
  template.content.querySelectorAll('b, i').forEach(el => {
    if (!(el instanceof HTMLElement)) return

    if (el.tagName === 'B' && isNeutralBoldWrapperFontWeight(el.style.fontWeight)) {
      el.replaceWith(...Array.from(el.childNodes))
    } else if (el.tagName === 'I' && el.style.fontStyle === 'normal') {
      el.replaceWith(...Array.from(el.childNodes))
    }
  })

  // Step 2: Convert bold/italic/underline expressed as inline styles on <span> elements
  // into semantic tags so they survive the style-strip that follows. Must run before
  // style removal while the style values are still readable.
  template.content.querySelectorAll('span').forEach(el => {
    if (!(el instanceof HTMLElement)) return

    const fw = el.style.fontWeight
    const fi = el.style.fontStyle
    const td = el.style.textDecoration
    const isBold = isBoldFontWeight(fw)
    const isItalic = fi === 'italic' || fi === 'oblique'
    const isUnderline = td.includes('underline')
    if (!isBold && !isItalic && !isUnderline) return

    let nodes: Node[] = Array.from(el.childNodes)
    if (isBold)      { const s = document.createElement('strong'); s.append(...nodes); nodes = [s] }
    if (isItalic)    { const e = document.createElement('em');     e.append(...nodes); nodes = [e] }
    if (isUnderline) { const u = document.createElement('u');      u.append(...nodes); nodes = [u] }
    el.replaceWith(...nodes)
  })

  // Step 3: Strip style and class from all remaining elements.
  template.content.querySelectorAll('[style], [class]').forEach(el => {
    el.removeAttribute('style')
    el.removeAttribute('class')
  })

  // Step 4: Unwrap <font> and now-bare <span> elements that carry no semantic meaning.
  template.content.querySelectorAll('font, span').forEach(el => {
    if (el.attributes.length === 0) {
      el.replaceWith(...Array.from(el.childNodes))
    }
  })

  return template.innerHTML
}
